<template>
    <form @submit.prevent="saveUser">
        <div class="card">
            <div class="card-body">
                <div class="new-employee-field">
                    <div class="row">
                        <!-- Full Name -->
                        <div class="mb-3">
                            <label class="form-label">Full Name</label>
                            <input
                                v-model="form.name"
                                type="text"
                                class="form-control"
                                :class="{ 'is-invalid': errors.name }"
                                name="name"
                            />
                            <span
                                v-if="errors.name"
                                class="invalid-feedback"
                                role="alert"
                            >
                                <strong>{{ errors.name[0] }}</strong>
                            </span>
                        </div>

                        <!-- Email -->
                        <div class="mb-3">
                            <label class="form-label">Email</label>
                            <input
                                v-model="form.email"
                                type="email"
                                class="form-control"
                                :class="{ 'is-invalid': errors.email }"
                                name="email"
                            />
                            <span
                                v-if="errors.email"
                                class="invalid-feedback"
                                role="alert"
                            >
                                <strong>{{ errors.email[0] }}</strong>
                            </span>
                        </div>

                        <!-- Contact Number -->
                        <div class="mb-3">
                            <label class="form-label">Contact Number</label>
                            <input
                                v-model="form.mobilenumber"
                                type="text"
                                class="form-control"
                                :class="{ 'is-invalid': errors.mobilenumber }"
                                name="mobilenumber"
                            />
                            <span
                                v-if="errors.mobilenumber"
                                class="invalid-feedback"
                                role="alert"
                            >
                                <strong>{{ errors.mobilenumber[0] }}</strong>
                            </span>
                        </div>

                        <!-- Designation -->
                        <div class="mb-3">
                            <label class="form-label">Designation</label>
                            <select
                                v-model="form.role"
                                class="form-control"
                                :class="{ 'is-invalid': errors.role }"
                                name="role"
                            >
                                <option value="">Choose</option>
                                <option
                                    v-for="role in availableRoles"
                                    :key="role"
                                    :value="role"
                                >
                                    {{ role }}
                                </option>
                            </select>
                            <span
                                v-if="errors.role"
                                class="invalid-feedback"
                                role="alert"
                            >
                                <strong>{{ errors.role[0] }}</strong>
                            </span>
                        </div>

                        <!-- Password -->
                        <div class="input-blocks mb-md-0 mb-sm-3">
                            <label>Password</label>
                            <div class="pass-group">
                                <input
                                    v-model="form.password"
                                    :type="showPassword ? 'text' : 'password'"
                                    class="pass-input form-control"
                                    :class="{ 'is-invalid': errors.password }"
                                    name="password"
                                />
                                <span
                                    class="fas toggle-password"
                                    :class="
                                        showPassword ? 'fa-eye' : 'fa-eye-slash'
                                    "
                                    @click="togglePassword"
                                    style="cursor: pointer"
                                ></span>
                                <span
                                    v-if="errors.password"
                                    class="invalid-feedback"
                                    role="alert"
                                >
                                    <strong>{{ errors.password[0] }}</strong>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer-btn">

            <button
                type="button"
                class="btn btn-cancel me-2"
                @click="cancelForm"
            >
                Cancel
            </button>
            <button type="submit" class="btn btn-submit" :disabled="loading">
                {{ loading ? "Creating..." : "Create" }}
            </button>
        </div>
    </form>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
    name: "EmployeeCreate",
    props: {
        shopId: {
            type: [Number, String],
            required: true,
        },
        roles: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            form: {
                name: "",
                email: "",
                password: "",
                mobilenumber: "",
                role: "",
            },
            errors: {},
            loading: false,
            showPassword: false,
        };
    },
    computed: {
        availableRoles() {
            // Filter out superAdmin role
            return this.roles.filter((role) => role !== "superAdmin");
        },
    },
    methods: {
        async saveUser() {
            this.loading = true;
            this.errors = {};

            try {
                console.log("Submitting employee form:", {
                    ...this.form,
                    shopId: this.shopId,
                });

                const response = await this.$http.post("/api/employees", {
                    ...this.form,
                    shopId: this.shopId,
                });

                // Handle success
                this.showSuccessMessage("User created successfully!");
                this.resetForm();

                // Emit event for parent component
                this.$emit("user-created", response.data);

                // Dispatch custom event for modal handling
                document.dispatchEvent(
                    new CustomEvent("employee-created", {
                        detail: response.data,
                    })
                );

                // Close modal if in modal context
                this.closeModal();

                // Redirect to shop page (if needed)
                if (response.data.redirect_url) {
                    setTimeout(() => {
                        window.location.href = response.data.redirect_url;
                    }, 1500);
                }
            } catch (error) {
                console.error("Employee creation error:", error);

                if (error.response) {
                    console.error("Error response:", error.response.data);
                    console.error("Error status:", error.response.status);

                    if (error.response.status === 422) {
                        // Validation errors
                        this.errors = error.response.data.errors;
                    } else if (error.response.status === 401) {
                        // Authentication error
                        this.showErrorMessage(
                            "Authentication failed. Please refresh the page and try again."
                        );
                    } else {
                        // Other errors
                        this.showErrorMessage(
                            error.response.data.message ||
                                "An error occurred while creating the user."
                        );
                    }
                } else {
                    // Network or other errors
                    this.showErrorMessage(
                        "Network error. Please check your connection and try again."
                    );
                    console.error("Network error:", error.message);
                }
            } finally {
                this.loading = false;
            }
        },

        resetForm() {
            this.form = {
                name: "",
                email: "",
                password: "",
                mobilenumber: "",
                role: "",
            };
            this.errors = {};
        },

        cancelForm() {
            this.resetForm();
            this.$emit("cancel");
            this.closeModal();
        },

        closeModal() {
            // If in modal, close it
            if (window.bootstrap && window.bootstrap.Modal) {
                const modal = window.bootstrap.Modal.getInstance(
                    this.$el.closest(".modal")
                );
                if (modal) {
                    modal.hide();
                }
            }
        },

        togglePassword() {
            this.showPassword = !this.showPassword;
        },

        async testAuth() {
            try {
                console.log("Testing authentication...");
                const response = await this.$http.get("/api/test-auth");
                console.log("Auth test successful:", response.data);
                this.showSuccessMessage("Authentication test successful!");
            } catch (error) {
                console.error("Auth test failed:", error);
                this.showErrorMessage(
                    "Authentication test failed: " +
                        (error.response?.data?.message || error.message)
                );
            }
        },

        showSuccessMessage,
        showErrorMessage,
    },

    mounted() {
        console.log("EmployeeCreate component mounted");
        console.log("Shop ID:", this.shopId);
        console.log("Available roles:", this.roles);

        // Mark element as mounted for loading state
        this.$el.closest("[data-vue-component]")?.classList.add("vue-mounted");
    },
};
</script>

<style scoped>
.pass-group {
    position: relative;
}

.toggle-password {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.invalid-feedback {
    display: block;
}
</style>
